import { appOn, type ChatMessage, canSaveToPenielly } from "@rhema/client";
import {
  ArrowUpIcon,
  BellIcon,
  CheckIcon,
  CopyIcon,
  cn,
  DownloadIcon,
  EditIcon,
  FileTextIcon,
  ForwardIcon,
  focusRing,
  isTyping,
  itemForShortcut,
  LinkIcon,
  type MenuAnchor,
  type MenuItem,
  MessageIcon,
  MoreIcon,
  PinIcon,
  ProductMark,
  ReplyIcon,
  SmileIcon,
  StarIcon,
  TrashIcon,
} from "@rhema/ui";
import { type KeyboardEvent, type ReactNode, useEffect, useState } from "react";
import { clockTime, firstName, isAdmin } from "../data/view";
import { TimeSheet } from "../later/TimeSheet";
import { remindChoices, whenLabel } from "../later/when";
import { useLinkStatus, useSaveToPenielly } from "../links/useLinks";
import { saveBlob } from "../media/prepare";
import { copyRich, copyText } from "../menus/clipboard";
import { deleteForMeAsk, deleteMessageAsk } from "../menus/confirms";
import { useFeedback } from "../menus/Feedback";
import { useClient, useClientState, useSession } from "../session/session";
import { isAdmin as isSpaceAdmin } from "../spaces/data";
import { ForwardSheet } from "./ForwardSheet";
import { MessageActions } from "./MessageActions";
import { MessageInfoSheet } from "./MessageInfoSheet";
import { isReadable, nameIn, textOf } from "./MessageList";
import { clipboardOf } from "./rich";
import { type ActionsOpen, useMessageActions } from "./useMessageActions";

/** The link that opens the chat at a message, for anyone in the chat. */
export function messageLink(origin: string, chatId: string, messageId: string): string {
  return `${origin}/m/${chatId}/${messageId}`;
}

/** The text a message's Copy text and Edit start from: its words, or a file's caption. */
export function wordsOf(m: ChatMessage): string {
  const c = m.content;
  if (c?.type === "text") return m.edited ?? c.body;
  if (c?.type === "media") return m.edited ?? c.caption ?? "";
  return textOf(m);
}

/** Whether you may edit a message: your own text, or the caption of your photo, video or file. */
export function isEditable(m: ChatMessage, meId: string | undefined): boolean {
  if (m.senderId !== meId || m.seq <= 0 || !isReadable(m)) return false;
  const c = m.content;
  return c?.type === "text" || (c?.type === "media" && c.kind !== "voice");
}

/** Who a message mentions: names picked from the list, and any member's @Name typed out in full. */
export function mentionsIn(
  body: string,
  members: { user: { id: string; display_name: string } }[],
  picked: Map<string, string> = new Map(),
): string[] {
  const ids = new Set<string>();
  for (const [name, id] of picked) if (body.includes(`@${name}`)) ids.add(id);
  for (const m of members) if (body.includes(`@${m.user.display_name}`)) ids.add(m.user.id);
  return [...ids];
}

function at(day: Date, hour: number): Date {
  const d = new Date(day);
  d.setHours(hour, 0, 0, 0);
  return d;
}

/** The times on Remind me, as the board lists them. */
function remindTimes(now = new Date()): { id: string; label: string; at: Date }[] {
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const monday = new Date(now);
  monday.setDate(now.getDate() + ((8 - now.getDay()) % 7 || 7));
  return [
    { id: "20m", label: "In 20 minutes", at: new Date(now.getTime() + 20 * 60_000) },
    { id: "1h", label: "In 1 hour", at: new Date(now.getTime() + 60 * 60_000) },
    { id: "3h", label: "In 3 hours", at: new Date(now.getTime() + 3 * 60 * 60_000) },
    { id: "tomorrow", label: "Tomorrow at 9:00", at: at(tomorrow, 9) },
    { id: "monday", label: "Next week on Monday at 9:00", at: at(monday, 9) },
  ];
}

/** What Save offers for a file, by its kind. */
function saveLabel(kind: "photo" | "video" | "file" | "voice"): string {
  return kind === "photo"
    ? "Save photo"
    : kind === "video"
      ? "Save video"
      : kind === "voice"
        ? "Save voice message"
        : "Save file";
}

const FORWARDABLE = new Set(["text", "media", "location", "contact"]);

export type MessageMenuOptions = {
  chatId: string;
  /** Every message the menu acts on: the chat's timeline, or a thread's first message and replies. */
  messages: ChatMessage[];
  /** Desktop opens the menu at the pointer and shows the hover bar. Phones lift the message. */
  desktop: boolean;
  /** Inside a thread, which has no quote replies, selecting or marking unread. */
  inThread?: { rootId: string };
  /** Quotes the message in the message box. */
  onReply?: (m: ChatMessage) => void;
  /** Opens the message's thread. Channels give it. */
  onThread?: (m: ChatMessage) => void;
  /** Puts your message in the message box to edit. */
  onEdit: (m: ChatMessage) => void;
  /** Starts selecting with this message. */
  onSelect?: (m: ChatMessage) => void;
  /** Asks Queravel about the message. Left out where Queravel cannot be asked. */
  onAsk?: (m: ChatMessage) => void;
  /** Writes an email about the message with Malakh. Left out where Malakh is off. */
  onEmail?: (m: ChatMessage) => void;
  /** After Mark unread from here, so the screen stops marking the chat read. */
  onMarkedUnread?: () => void;
};

/**
 * One message menu for a chat, a channel and a thread: its items in the board's order, the
 * shortcuts that run them on a focused message, the hover bar on desktop, and the sheets they open.
 */
export function useMessageMenu(o: MessageMenuOptions) {
  const { chatId, messages, desktop, inThread } = o;
  const client = useClient();
  const state = useClientState();
  const feedback = useFeedback();
  const { platform } = useSession();
  const chat = state.chats[chatId];
  const meId = state.me?.id;
  const [open, setOpen] = useState<(ActionsOpen & { picking?: boolean }) | null>(null);
  const [forwarding, setForwarding] = useState<ChatMessage[] | null>(null);
  const [infoFor, setInfoFor] = useState<ChatMessage | null>(null);
  const [remindFor, setRemindFor] = useState<ChatMessage | null>(null);
  const penielly = useSaveToPenielly(chatId);
  const peniellyStatus = useLinkStatus("penielly").status;
  const origin = platform.serverOrigin() || window.location.origin;

  // Your stars in this chat, for Star and Unstar and the star beside the time.
  const kind = chat?.kind;
  const starsLoaded = state.stars?.[chatId] !== undefined;
  useEffect(() => {
    if (!kind || kind === "assistant" || starsLoaded) return;
    void client.loadStars(chatId).catch(() => undefined);
  }, [client, chatId, kind, starsLoaded]);
  const starred = new Set(state.stars?.[chatId] ?? []);

  const notify = (text: string, undo?: () => void) => feedback.notify(text, undo);
  const fail = (e: unknown) => notify((e as Error)?.message || "That did not work. Try again.");

  const holdProps = useMessageActions(
    (id, at, opener) => setOpen({ id, at, opener }),
    (id, e: KeyboardEvent<HTMLElement>) => {
      if (open || isTyping(e.target) || e.target !== e.currentTarget) return false;
      const m = messages.find((x) => x.id === id);
      if (!m || !isReadable(m)) return false;
      const hit = itemForShortcut(itemsFor(m), e.nativeEvent);
      if (!hit || hit.type === "submenu") return false;
      // Copy leaves text the person selected to the browser's own copy.
      if (hit.id === "copy" && window.getSelection()?.toString()) return false;
      hit.onSelect();
      return true;
    },
  );

  if (!chat) {
    return {
      holdProps,
      itemsFor: () => [] as MenuItem[],
      toolbar: () => null,
      starred,
      overlays: null as ReactNode,
      forward: (_list: ChatMessage[]) => undefined,
      copy: (_list: ChatMessage[]) => undefined,
      hide: (_list: ChatMessage[]) => undefined,
      star: (_list: ChatMessage[]) => undefined,
      canSaveToPenielly: (_m: ChatMessage) => false,
      saveToPenielly: (_list: ChatMessage[]) => undefined,
      react: (_m: ChatMessage, _emoji: string) => undefined,
    };
  }

  const channel = chat.kind === "channel";
  const group = chat.kind === "group" || channel;
  const pins = state.pins[chatId] ?? [];
  const canPin = !chat.only_admins_post || isAdmin(chat);
  const owningSpace = channel ? state.spaces.find((s) => s.id === chat.space_id) : undefined;
  const peniellyOn = appOn(state, chat, "penielly");

  const deletable = (m: ChatMessage) =>
    m.senderId === meId || (group && isAdmin(chat)) || (channel && isSpaceAdmin(owningSpace));
  const penniellySavable = (m: ChatMessage) => {
    const c = m.content;
    return Boolean(
      peniellyStatus?.available &&
        peniellyOn &&
        c?.type === "media" &&
        (c.kind === "photo" || c.kind === "video") &&
        m.senderId !== meId &&
        canSaveToPenielly(c.media.mime),
    );
  };

  const react = (m: ChatMessage, emoji: string) => {
    const mine = Object.entries(m.reactions).find(([, users]) => meId && users.includes(meId))?.[0];
    setOpen(null);
    void client
      .react(chatId, m.id, emoji, emoji === mine)
      .catch(() => notify("That reaction did not go through. Try again."));
  };

  // Copy text keeps formatting. The plain text is the message as written, marks and all, so
  // pasting into a plain text field or an older Rhema shows what was sent. Apps that read HTML,
  // Rhema's message box among them, get the formatting itself.
  const copy = (list: ChatMessage[]) => {
    const parts = list.map((m) => clipboardOf(m, wordsOf(m), chat));
    const text = parts.map((p) => p.text).join("\n\n");
    const html = parts.map((p) => p.html).join("");
    void copyRich(text, html).then((note) => notify(note));
  };

  const star = (list: ChatMessage[], on = true) => {
    const ids = list.filter((m) => m.seq > 0).map((m) => m.id);
    void Promise.all(ids.map((id) => client.starMessage(chatId, id, on)))
      .then(() =>
        notify(
          on ? (ids.length === 1 ? "Starred." : `${ids.length} messages starred.`) : "Unstarred.",
          () => void Promise.all(ids.map((id) => client.starMessage(chatId, id, !on))).catch(fail),
        ),
      )
      .catch(fail);
  };

  const hide = (list: ChatMessage[]) => {
    const ids = list.filter((m) => m.seq > 0).map((m) => m.id);
    if (!ids.length) return;
    void feedback.confirm(deleteForMeAsk(ids.length)).then((yes) => {
      if (yes) void client.hideMessages(chatId, ids).catch(fail);
    });
  };

  const saveToPenielly = (list: ChatMessage[]) => {
    if (peniellyStatus && !peniellyStatus.linked) {
      notify("Link Penielly in Settings to save photos there.");
      return;
    }
    const targets = list.flatMap((m) =>
      m.content?.type === "media" ? [{ media: m.content.media, capturedAt: m.createdAt }] : [],
    );
    void penielly.save(targets).then(notify);
  };

  const saveFile = async (m: ChatMessage) => {
    const c = m.content;
    if (c?.type !== "media") return;
    try {
      const blob = m.localFile ?? (await client.media.download(c.media));
      const ext = c.media.mime.split("/").pop()?.split(";")[0] || "bin";
      const name =
        c.media.name ||
        `${c.kind === "file" ? "File" : c.kind === "voice" ? "Voice message" : c.kind === "photo" ? "Photo" : "Video"}.${ext}`;
      // Phones save through the share sheet, which offers Save Image and Save to Files.
      const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
      const file = typeof File === "function" ? new File([blob], name, { type: c.media.mime }) : null;
      if (!desktop && file && nav.canShare?.({ files: [file] }) && nav.share) {
        try {
          await nav.share({ files: [file] });
        } catch (err) {
          if ((err as Error).name !== "AbortError") throw err;
        }
        return;
      }
      saveBlob(blob, name);
    } catch (err) {
      fail(err);
    }
  };

  const remind = (m: ChatMessage, when: Date) =>
    void client
      .remind(chatId, m.id, when)
      .then(() => notify(`Reminder set for ${whenLabel(when).toLowerCase()}.`))
      .catch(fail);

  const glyph = { size: 18, strokeWidth: 2.2 } as const;

  /** A message's items, in the order the board gives. Items that cannot apply are left out. */
  const itemsFor = (m: ChatMessage): MenuItem[] => {
    const own = m.senderId === meId;
    const sent = m.seq > 0;
    const c = m.content;
    const pinned = pins.some((p) => p.message.id === m.id);
    const isStarred = starred.has(m.id);
    const canThread = channel && !inThread && Boolean(o.onThread) && !m.threadRoot;
    const askable = Boolean(o.onAsk) && !m.expiresAt && c?.type !== "notice";
    const sender = firstName(nameIn(chat, m.senderId, meId));
    const list: (MenuItem | false)[] = [
      !inThread &&
        !!o.onReply && {
          id: "reply",
          label: "Reply",
          shortcut: "R",
          icon: <ReplyIcon {...glyph} />,
          onSelect: () => o.onReply?.(m),
        },
      canThread && {
        id: "thread",
        label: "Reply in thread",
        shortcut: "T",
        icon: <MessageIcon {...glyph} />,
        onSelect: () => o.onThread?.(m),
      },
      sent &&
        !!c &&
        FORWARDABLE.has(c.type) && {
          id: "forward",
          label: "Forward…",
          shortcut: "F",
          icon: <ForwardIcon {...glyph} />,
          onSelect: () => setForwarding([m]),
        },
      wordsOf(m).length > 0 && {
        id: "copy",
        label: "Copy text",
        shortcut: "Mod+C",
        icon: <CopyIcon {...glyph} />,
        onSelect: () => copy([m]),
      },
      sent && {
        id: "link",
        label: "Copy link to message",
        shortcut: "L",
        icon: <LinkIcon {...glyph} />,
        onSelect: () =>
          void copyText(
            messageLink(origin, chatId, m.id),
            "Link copied. It opens for people in this chat.",
          ).then((note) => notify(note)),
      },
      { type: "separator", id: "s1" },
      sent &&
        canPin && {
          id: pinned ? "unpin" : "pin",
          label: pinned ? "Unpin" : "Pin for everyone",
          shortcut: "P",
          icon: <PinIcon {...glyph} />,
          onSelect: () =>
            pinned
              ? void client
                  .unpin(chatId, m.id)
                  .then(() => notify("Unpinned."))
                  .catch(fail)
              : void client
                  .pin(chatId, m.id)
                  .then(() =>
                    notify(
                      "Pinned for everyone in this chat.",
                      () => void client.unpin(chatId, m.id).catch(fail),
                    ),
                  )
                  .catch(fail),
        },
      sent && {
        id: isStarred ? "unstar" : "star",
        label: isStarred ? "Unstar" : "Star",
        shortcut: "S",
        icon: <StarIcon {...glyph} />,
        onSelect: () => star([m], !isStarred),
      },
      sent && {
        type: "submenu",
        id: "remind",
        label: "Remind me",
        icon: <BellIcon {...glyph} />,
        items: [
          ...remindTimes().map((t) => ({
            id: `remind-${t.id}`,
            label: t.label,
            onSelect: () => remind(m, t.at),
          })),
          { type: "separator" as const, id: "remind-s" },
          { id: "remind-pick", label: "Pick a time…", onSelect: () => setRemindFor(m) },
        ],
      },
      sent &&
        !inThread && {
          id: "unread",
          label: "Mark unread from here",
          shortcut: "U",
          icon: <ArrowUpIcon {...glyph} />,
          onSelect: () =>
            void client
              .markUnreadFrom(chatId, m.id)
              .then(() => {
                o.onMarkedUnread?.();
                notify("Marked unread from this message.");
              })
              .catch(fail),
        },
      !inThread &&
        !!o.onSelect && {
          id: "select",
          label: "Select",
          icon: <CheckIcon {...glyph} />,
          onSelect: () => o.onSelect?.(m),
        },
      isEditable(m, meId) &&
        !(inThread && m.id === inThread.rootId) && {
          id: "edit",
          label: "Edit",
          shortcut: "E",
          icon: <EditIcon {...glyph} />,
          onSelect: () => o.onEdit(m),
        },
      { type: "separator", id: "s2" },
      askable && {
        id: "ask",
        label: "Ask Queravel",
        shortcut: "Mod+J",
        icon: <ProductMark product="queravel" size={22} className="flex-none" />,
        onSelect: () => o.onAsk?.(m),
      },
      !!o.onEmail && {
        id: "email",
        label: "Email",
        icon: <ProductMark product="malak" size={22} className="flex-none" />,
        onSelect: () => o.onEmail?.(m),
      },
      penniellySavable(m) && {
        id: "penielly",
        label: "Save to Penielly",
        icon: <ProductMark product="penielly" size={22} className="flex-none" />,
        onSelect: () => saveToPenielly([m]),
      },
      sent &&
        c?.type === "media" && {
          id: "save",
          label: saveLabel(c.kind),
          icon: <DownloadIcon {...glyph} />,
          onSelect: () => void saveFile(m),
        },
      own &&
        sent &&
        chat.kind !== "self" &&
        chat.kind !== "assistant" && {
          id: "info",
          label: "Message info",
          icon: <FileTextIcon {...glyph} />,
          onSelect: () => setInfoFor(m),
        },
      { type: "separator", id: "s3" },
      sent && {
        id: "hide",
        label: "Delete for me…",
        tone: "danger",
        icon: <TrashIcon {...glyph} />,
        onSelect: () => hide([m]),
      },
      sent &&
        deletable(m) && {
          id: "delete",
          label: "Delete for everyone…",
          tone: "danger",
          ...(own ? { shortcut: "Delete" } : {}),
          icon: <TrashIcon {...glyph} />,
          onSelect: () =>
            void feedback
              .confirm(deleteMessageAsk(inThread ? "thread" : channel ? "channel" : "chat"))
              .then(async (yes) => {
                if (yes) await client.deleteMessage(chatId, m.id);
              })
              .catch(fail),
        },
      sent &&
        !own &&
        !!m.senderId &&
        chat.kind !== "self" &&
        chat.kind !== "assistant" && {
          id: "report",
          label: "Report message…",
          tone: "danger",
          onSelect: () =>
            feedback.report({
              userId: m.senderId ?? undefined,
              chatId,
              name: sender,
              message: { id: m.id, text: wordsOf(m) || textOf(m) },
            }),
        },
    ];
    const out: MenuItem[] = [];
    for (const i of list) {
      if (!i) continue;
      if (i.type === "separator" && (!out.length || out[out.length - 1]?.type === "separator")) continue;
      out.push(i);
    }
    while (out[out.length - 1]?.type === "separator") out.pop();
    return out;
  };

  const openAt = (id: string, el: HTMLElement, picking = false) => {
    const box = el.getBoundingClientRect();
    setOpen({ id, at: { x: box.left, y: box.bottom + 4 }, opener: el, picking });
  };

  /** The hover bar: React, Reply, Reply in thread and the full menu. */
  const toolbar = (m: ChatMessage): ReactNode => {
    if (!desktop || !isReadable(m) || m.seq <= 0) return null;
    const button = (label: string, icon: ReactNode, onClick: (el: HTMLElement) => void) => (
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e.currentTarget);
        }}
        className={cn(
          "flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-ink-secondary hover:bg-surface-header hover:text-ink",
          focusRing,
        )}
      >
        {icon}
      </button>
    );
    const canThread = channel && !inThread && Boolean(o.onThread) && !m.threadRoot;
    return (
      <div
        role="toolbar"
        aria-label="Message actions"
        className="flex items-center gap-0.5 rounded-full border border-line bg-surface p-0.5 shadow-menu"
      >
        {button("React", <SmileIcon size={17} />, (el) => openAt(m.id, el, true))}
        {!inThread && o.onReply && button("Reply", <ReplyIcon size={17} />, () => o.onReply?.(m))}
        {canThread && button("Reply in thread", <MessageIcon size={17} />, () => o.onThread?.(m))}
        {button("More actions", <MoreIcon size={17} />, (el) => openAt(m.id, el))}
      </div>
    );
  };

  const target = open ? messages.find((m) => m.id === open.id) : undefined;
  const myReaction = target
    ? Object.entries(target.reactions).find(([, users]) => meId && users.includes(meId))?.[0]
    : undefined;
  const anchor: MenuAnchor | null = desktop && open ? open.at : null;

  const overlays = (
    <>
      {target && isReadable(target) && (
        <MessageActions
          key={`${target.id}-${open?.picking ? "pick" : "menu"}`}
          text={textOf(target)}
          time={clockTime(target.createdAt)}
          own={target.senderId === meId}
          items={itemsFor(target)}
          reaction={myReaction}
          picking={open?.picking}
          onReact={(emoji) => react(target, emoji)}
          onClose={() => setOpen(null)}
          anchor={anchor}
          opener={open?.opener}
        />
      )}
      <ForwardSheet
        messages={forwarding}
        onClose={() => setForwarding(null)}
        onDone={(note) => {
          setForwarding(null);
          notify(note);
        }}
      />
      <MessageInfoSheet chat={chat} message={infoFor} onClose={() => setInfoFor(null)} />
      <TimeSheet
        open={remindFor !== null}
        title="Remind me"
        subtitle="You get a notification that says only that a reminder is due."
        choices={remindChoices()}
        pickLabel="Set the reminder"
        onPick={(when) => {
          const m = remindFor;
          setRemindFor(null);
          if (m) remind(m, when);
        }}
        onClose={() => setRemindFor(null)}
      />
    </>
  );

  return {
    holdProps,
    itemsFor,
    toolbar,
    starred,
    overlays,
    forward: (list: ChatMessage[]) => setForwarding(list),
    copy,
    hide,
    star: (list: ChatMessage[]) => star(list, true),
    canSaveToPenielly: penniellySavable,
    saveToPenielly,
    react,
  };
}
